import React, {ReactNode} from 'react';

import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';

import { OBSERVE_LANGUAGE } from './constants';

import {RefetchContext} from './RefetchContext';
import { IRefetchItem } from './types';

type Props = {
  children: ReactNode;
};

export const RefetchProvider = ({children}: Props) => {
  const list = React.useRef<IRefetchItem[]>([]);

  const {i18n} = useTranslation();
  const queryClient = useQueryClient();

  const addItem = React.useCallback((item: IRefetchItem) => {
    list.current = list.current.concat(item);
  }, []);

  const removeItem = React.useCallback((item: IRefetchItem) => {
    list.current = list.current.filter((itemFiltering) => itemFiltering.name !== item.name);
  }, []);

  const provideValue = React.useMemo(
    () => ({
      addItem,
      removeItem,
    }),
    [addItem, removeItem]
  );

  /** Observe i18n */
  React.useEffect(() => {
    const onlanguageChange = () => {
      const i18nRefetchList = list.current.filter((itemFiltering) => itemFiltering.params.observe.includes(OBSERVE_LANGUAGE));
      const queryKeys = i18nRefetchList.map((itemMapping) => itemMapping.name);

      if (queryKeys.length) {
        queryClient.invalidateQueries({
          queryKey: queryKeys,
        });
      }
    };

    i18n.on('languageChanged', onlanguageChange);

    return () => {
      i18n.off('languageChanged', onlanguageChange);
    };
  }, [i18n, queryClient]);

  return <RefetchContext.Provider value={provideValue}>{children}</RefetchContext.Provider>;
};
